import React, { useState, useEffect } from 'react';
import { studentApi } from '../../services/api';

const F = { fontFamily: 'Inter' } as const;

interface ClassRow {
  id: number | string;
  name: string;
  code?: string;
  campuses?: { name?: string; institutions?: { name?: string } };
  class_student_enrollments?: { length: number };
  student_count?: number;
  leave_requested?: boolean;
}

const StudentClasses: React.FC = () => {
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Join modal
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState<string | null>(null);

  // Leave modal — leaving is a request the teacher must approve. A card whose
  // enrollment already has a pending request shows a cancel flow instead.
  const [leaveTarget, setLeaveTarget] = useState<ClassRow | null>(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [leaveDone, setLeaveDone] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    studentApi.classes()
      .then((res) => setClasses(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setJoinLoading(true);
    setJoinError(null);
    setJoinSuccess(null);
    try {
      const res = await studentApi.joinClass(joinCode.trim());
      setJoinSuccess(`"${res.data.className}" sınıfına başarıyla katıldın!`);
      setJoinCode('');
      load();
    } catch (e: any) {
      setJoinError(e?.response?.data?.error ?? 'Bir hata oluştu.');
    } finally {
      setJoinLoading(false);
    }
  };

  const closeLeave = () => { setLeaveTarget(null); setLeaveError(null); setLeaveDone(null); };

  // Send a leave request to the teacher (card without a pending request).
  const handleRequestLeave = async () => {
    if (!leaveTarget) return;
    setLeaveLoading(true);
    setLeaveError(null);
    try {
      await studentApi.requestLeaveClass(leaveTarget.id);
      setLeaveDone('Ayrılma isteğin öğretmenine iletildi. Öğretmenin onayladığında sınıftan çıkarılacaksın.');
      load();
    } catch (e: any) {
      setLeaveError(e?.response?.data?.error ?? 'Bir hata oluştu.');
    } finally {
      setLeaveLoading(false);
    }
  };

  // Cancel a pending leave request (card that already shows "beklemede").
  const handleCancelRequest = async () => {
    if (!leaveTarget) return;
    setLeaveLoading(true);
    setLeaveError(null);
    try {
      await studentApi.cancelLeaveRequest(leaveTarget.id);
      setLeaveDone('Ayrılma isteğin iptal edildi.');
      load();
    } catch (e: any) {
      setLeaveError(e?.response?.data?.error ?? 'Bir hata oluştu.');
    } finally {
      setLeaveLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Sınıflarım</h1>
        <button
          onClick={() => { setJoinOpen(true); setJoinError(null); setJoinSuccess(null); setJoinCode(''); }}
          className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:opacity-90 transition-opacity"
          style={{ background: '#e5599b' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#fafafa" strokeWidth="2.5" strokeLinecap="round">
            <line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" />
          </svg>
          <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Sınıfa Katıl</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : classes.length === 0 ? (
        <p className="text-center text-gray-400 py-16">Henüz bir sınıfa katılmadın.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {classes.map((c, i) => {
            const institution = c.campuses?.institutions?.name ?? c.campuses?.name ?? '';
            return (
              <div key={c.id}
                className="bg-[#fafafa] rounded-[20px] overflow-hidden"
                style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="h-[103px] overflow-hidden">
                  <img src={`/a-sinif-tiling${(i % 4) + 1}.png`} alt="" className="w-full h-full object-cover object-center" />
                </div>
                <div className="p-4">
                  <p style={{ ...F, fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>{institution || c.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{c.name}</p>
                    {/* Three-dot leave button */}
                    <button
                      onClick={() => { setLeaveTarget(c); setLeaveError(null); setLeaveDone(null); }}
                      className="w-[32px] h-[32px] rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors text-[#2b2b2b] text-lg font-bold shrink-0"
                      title={c.leave_requested ? 'Ayrılma isteği beklemede' : 'Sınıftan Ayrıl'}
                    >
                      ⋮
                    </button>
                  </div>
                  {c.leave_requested && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
                      style={{ background: '#fff4e0', border: '1px solid #f0b34a' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#e08c00' }} />
                      <span style={{ ...F, fontSize: 12, fontWeight: 600, color: '#b3730a' }}>Ayrılma isteği beklemede</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Join Class Modal ── */}
      {joinOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setJoinOpen(false)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[480px] p-7"
            style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
            <button onClick={() => setJoinOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-200 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>

            <h2 style={{ ...F, fontSize: 22, fontWeight: 700, color: '#3973bf', marginBottom: 20 }}>Sınıfa Katıl</h2>

            {joinSuccess ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-[#59e56e] flex items-center justify-center mx-auto mb-4">
                  <svg width="32" height="24" viewBox="0 0 32 24" fill="none">
                    <path d="M2 12L12 22L30 4" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 20 }}>{joinSuccess}</p>
                <button onClick={() => setJoinOpen(false)}
                  className="rounded-[12px] px-8 py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
                  <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Tamam</span>
                </button>
              </div>
            ) : (
              <>
                <p style={{ ...F, fontSize: 14, color: '#888', marginBottom: 16 }}>
                  Öğretmeninden aldığın sınıf kodunu gir.
                </p>
                <input
                  value={joinCode}
                  onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleJoin()}
                  placeholder="Örn: ABC12345"
                  maxLength={20}
                  className="w-full bg-white rounded-[14px] px-4 py-3 outline-none text-[18px] font-mono tracking-widest mb-3"
                  style={{ border: '2px solid #3973bf', ...F }}
                />
                {joinError && <p style={{ ...F, fontSize: 13, color: '#e5599b', marginBottom: 12 }}>{joinError}</p>}
                <div className="flex gap-3">
                  <button onClick={() => setJoinOpen(false)}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90" style={{ background: '#e5e5e5' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
                  </button>
                  <button onClick={handleJoin} disabled={!joinCode.trim() || joinLoading}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-40" style={{ background: '#e5599b' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>
                      {joinLoading ? 'Katılınıyor…' : 'Katıl'}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Leave Class Modal ── */}
      {/* Leaving is a request the teacher approves. If a request is already
          pending, this modal offers to cancel it instead. */}
      {leaveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={closeLeave} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[420px] p-7"
            style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
            {leaveDone ? (
              <>
                <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#3973bf', marginBottom: 12 }}>Bilgilendirme</h2>
                <p style={{ ...F, fontSize: 15, color: '#2b2b2b', marginBottom: 20 }}>{leaveDone}</p>
                <button onClick={closeLeave}
                  className="w-full rounded-[12px] py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
                  <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Tamam</span>
                </button>
              </>
            ) : leaveTarget.leave_requested ? (
              <>
                <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#b3730a', marginBottom: 12 }}>Ayrılma İsteği</h2>
                <p style={{ ...F, fontSize: 15, color: '#2b2b2b', marginBottom: 20 }}>
                  <strong>{leaveTarget.name}</strong> sınıfı için ayrılma isteğin öğretmenin onayında bekliyor. İsteği iptal etmek ister misin?
                </p>
                {leaveError && <p style={{ ...F, fontSize: 13, color: '#e5599b', marginBottom: 12 }}>{leaveError}</p>}
                <div className="flex gap-3">
                  <button onClick={closeLeave}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90" style={{ background: '#e5e5e5' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>Vazgeç</span>
                  </button>
                  <button onClick={handleCancelRequest} disabled={leaveLoading}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-40" style={{ background: '#e5599b' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>
                      {leaveLoading ? 'İptal ediliyor…' : 'İsteği İptal Et'}
                    </span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#e5599b', marginBottom: 12 }}>Sınıftan Ayrıl</h2>
                <p style={{ ...F, fontSize: 15, color: '#2b2b2b', marginBottom: 20 }}>
                  <strong>{leaveTarget.name}</strong> sınıfından ayrılmak için öğretmenine bir istek gönderilecek. Öğretmenin onayladığında sınıftan çıkarılacaksın. Devam edilsin mi?
                </p>
                {leaveError && <p style={{ ...F, fontSize: 13, color: '#e5599b', marginBottom: 12 }}>{leaveError}</p>}
                <div className="flex gap-3">
                  <button onClick={closeLeave}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90" style={{ background: '#e5e5e5' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>Vazgeç</span>
                  </button>
                  <button onClick={handleRequestLeave} disabled={leaveLoading}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-40" style={{ background: '#e5599b' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>
                      {leaveLoading ? 'Gönderiliyor…' : 'İstek Gönder'}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentClasses;
